import { FunctionComponent, PropsWithChildren, ReactNode, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { InView } from 'react-intersection-observer';
import { IUISupplier } from '@bridebook/toolbox/src/types';
import { Box, FelaCSS } from '@bridebook/ui';
import SaveToShortlistButton from '@bridebook/web/app-shared/components/save-to-shortlist-button/save-to-shortlist-button';
import {
  SupplierCardInView,
  TRefSupplier,
  ViewedEndScreen,
} from '@bridebook/web/app-shared/lib/supplier/types';
import { isSearchResultsPage } from '@bridebook/web/lib/search/utils';
import { getIsCountrySearch } from '@bridebook/web/lib/search/utils/get-is-country-search';
import { ShortlistedLocation } from '@bridebook/web/lib/shortlist/types';
import { getSupplierPhotos, getSupplierThumb } from '@bridebook/web/lib/supplier/utils';
import { ISearchState, RecommendationsEngineVersion } from '@bridebook/web/lib/types';
import { IconClass } from '@bridebook/web/lib/ui/constants';
import BudgetMatchLabel from './budget-match-label';
import ContentFrame from './content-frame';
import DeleteButton from './delete-button';
import FeaturedMarker from './featured-marker';
import LightweightCarousel from './lightweight-carousel';
import componentStyles from './supplier-card.style';
import { useSupplierBucketSetting } from '@bridebook/web/lib/features/use-supplier-bucket-setting';

const CAROUSEL_MAX_IMAGES = 2;

export interface ICommonProps {
  budget?: number | null;
  enquiryCTAText?: string;
  isLoggedIn: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isUK: boolean;
  locationCounty?: string;
  locationData: ISearchState['locationData'];
  onCarouselInView: (opts: SupplierCardInView) => void;
  onCarouselUsed: (supplier: IUISupplier, photos: string[], currentImageUrl?: string) => void;
  onContactSupplierClick: (
    ev: React.MouseEvent<ReactNode, MouseEvent>,
    supplier: IUISupplier,
  ) => void;
  onViewedEndScreen: (opts: ViewedEndScreen) => void;
  pathname: string;
  searchLocation: string;
  useIsShortlisted: (supplierId: IUISupplier['id']) => boolean;
}

export interface ISharedTypes extends ICommonProps {
  toggleHoverBlock?: () => any;
  showCarousel?: boolean;
  refSupplier?: TRefSupplier;
  supplierCardOnClick?: (supplier: IUISupplier, e?: SyntheticEvent<any>) => any;
  multiLineStyle?: FelaCSS;
  noAnimation?: boolean;
  shortlistedLocation?: ShortlistedLocation;
  viewedLocation?: ShortlistedLocation;
  showBudgetMatch?: boolean;
  eagerLoad?: boolean;
  style?: FelaCSS;
  onDelete?: (item: IUISupplier) => void;
  onShortlist?: () => void;
  add?: () => void;
  remove?: () => void;
  trackIntersection?: boolean;
  supplierTileOrder?: number;
  fullBleed?: boolean;
  nameWidth?: number;
  recommendationEngineUsed?: boolean;
  recommendationEngineVersion?: RecommendationsEngineVersion;
  shortlistButtonVariantSize?: 'sm' | 'standard';
}

export interface IProps extends ISharedTypes {
  supplier: IUISupplier;
}

const SupplierCard: FunctionComponent<PropsWithChildren<IProps>> = ({
  showCarousel,
  supplier,
  supplier: { town, featured, type, supplierBoosted },
  supplierCardOnClick,
  isMobile,
  isTablet,
  children,
  viewedLocation,
  showBudgetMatch,
  onDelete,
  style: passThroughStyle = {},
  onShortlist,
  trackIntersection,
  supplierTileOrder,
  add,
  remove,
  onCarouselUsed,
  pathname: currentPath,
  searchLocation,
  fullBleed,
  locationData,
  locationCounty,
  isLoggedIn,
  isUK,
  nameWidth,
  eagerLoad,
  refSupplier,
  onCarouselInView,
  budget,
  onViewedEndScreen,
  useIsShortlisted,
  recommendationEngineUsed,
  recommendationEngineVersion,
  shortlistButtonVariantSize = 'sm',
}: PropsWithChildren<IProps>) => {
  const MIN_DISTANCE = 40;
  const MAX_DISTANCE = 70;
  const { t } = useTranslation();
  const isVenue = type === 'venue';
  const isSearch = isSearchResultsPage(currentPath);
  const isShortlisted = useIsShortlisted(supplier.id);
  const valueSearch = searchLocation !== '';
  const isCountrySearched = getIsCountrySearch(searchLocation);

  const { isUnlocked: isFeatureSupplierMarkerUnlocked } = useSupplierBucketSetting(
    'featuredSupplierMarker',
    supplier.type,
    supplier.tier,
  );
  const showFeaturedMarker = (supplierBoosted || isFeatureSupplierMarkerUnlocked) && !onDelete;
  const thumb: string = getSupplierThumb(supplier);
  const photos = getSupplierPhotos(supplier);
  const toggle = (isShortlisted: boolean) => {
    if (isShortlisted) {
      if (remove) remove();
    } else {
      if (add) add();
    }
  };

  const displaySupplierTown = (() => {
    if (!town) return false;
    return (
      !isLoggedIn ||
      !isUK ||
      !isVenue ||
      ((!supplier.isForeign || isCountrySearched) &&
        (supplier.distanceFromOrigin ?? 0) > MAX_DISTANCE)
    );
  })();

  const onCarouselUsedProp = (newIndex: number) => {
    // Sliced to CAROUSEL_MAX_IMAGES to match the LightweightCarousel to avoid undefined carouselImages[index].
    const carouselImages = [thumb, ...photos].slice(0, CAROUSEL_MAX_IMAGES);
    onCarouselUsed(supplier, photos, carouselImages[newIndex]);
  };

  const onDeleteClick = (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof onDelete === 'function') {
      onDelete(supplier);
    }
  };

  const locationMark =
    isSearch &&
    supplier.isForeign &&
    (!supplier.distanceFromOrigin ||
      (supplier.distanceFromOrigin > MIN_DISTANCE && supplier.distanceFromOrigin < MAX_DISTANCE))
      ? `${t('supplierLocationTagNear')} ${
          locationData[searchLocation] !== undefined
            ? locationData[searchLocation].county
            : locationCounty === undefined
              ? searchLocation
              : locationCounty
        }`
      : town;

  const featuredSupplier = t('featuredSupplier', {
    supplierType: supplier.type,
    supplierTown: supplier.county !== undefined ? supplier.county : searchLocation,
  });

  const displayLocationMark =
    !displaySupplierTown &&
    locationMark &&
    isVenue &&
    (!supplier.distanceFromOrigin || supplier.distanceFromOrigin < MAX_DISTANCE) &&
    isLoggedIn &&
    isUK;

  const displayFeaturedSupplier =
    !displaySupplierTown &&
    !displayLocationMark &&
    !isCountrySearched &&
    isLoggedIn &&
    isUK &&
    valueSearch &&
    isVenue &&
    (!supplier.distanceFromOrigin || supplier.distanceFromOrigin > MAX_DISTANCE) &&
    supplier.isForeign;

  const displayShortlistButton = add && remove;

  const styles = componentStyles({
    isMobile,
    fullBleed,
    nameWidth,
  });

  const shortlistButtonProps = {
    isLoggedIn,
    isShortlisted,
  };

  const content = (
    <LightweightCarousel
      isVenue={isVenue}
      showOverlay
      suppliedImages={[thumb, ...photos]}
      showCarousel={showCarousel && (isMobile || isTablet)}
      onCarouselUsed={onCarouselUsedProp}
      maxNumber={CAROUSEL_MAX_IMAGES}
      eagerLoad={eagerLoad}
      showGradient
      fullBleed={fullBleed}
      onViewedEndScreen={onViewedEndScreen}
      isMobile={isMobile}
      isTablet={isMobile}
      supplierId={supplier.id}>
      {!fullBleed && (
        <Box style={styles.imgContentWrap}>
          <Box style={styles.statsWrap}>
            {displayLocationMark && (
              <Box style={styles.location} dataTest="location-mark">
                <span className={IconClass.LocationFilled} style={styles.locationIcon} />
                <Box style={styles.townName}>{locationMark}</Box>
              </Box>
            )}
            {displaySupplierTown && (
              <Box style={styles.location}>
                <span className={IconClass.LocationFilled} style={styles.locationIcon} />
                <Box style={styles.townName}>{supplier.town}</Box>
              </Box>
            )}
            {displayFeaturedSupplier && (
              <Box style={styles.featured}>
                <span className={IconClass.LocationFilled} style={styles.locationIcon} />
                <Box style={styles.featuredTownName}>{featuredSupplier}</Box>
              </Box>
            )}
            {displayShortlistButton && (
              <Box style={styles.saveToShort}>
                <SaveToShortlistButton
                  {...shortlistButtonProps}
                  supplierId={supplier.id}
                  onShortlist={onShortlist}
                  toggle={toggle}
                  variantSize={shortlistButtonVariantSize}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}

      {fullBleed && displayShortlistButton && (
        <Box style={styles.saveToShort}>
          <SaveToShortlistButton
            {...shortlistButtonProps}
            supplierId={supplier.id}
            onShortlist={onShortlist}
            toggle={toggle}
            variantSize={shortlistButtonVariantSize}
          />
        </Box>
      )}

      {showBudgetMatch && <BudgetMatchLabel supplier={supplier} budget={budget} />}
      {onDelete && (
        <Box style={styles.deleteWrap}>
          <DeleteButton onDelete={onDeleteClick} fullBleed={fullBleed} />
        </Box>
      )}
      {showFeaturedMarker && (
        <Box data-name="featured-line" style={styles.featuredMarkerWrap}>
          <FeaturedMarker />
        </Box>
      )}
    </LightweightCarousel>
  );

  const card = (
    <ContentFrame
      topContent={content}
      featured={featured}
      isMobile={isMobile}
      passThroughStyle={passThroughStyle}
      supplier={supplier}
      supplierCardOnClick={supplierCardOnClick}
      refSupplier={refSupplier}
      fullBleed={fullBleed}>
      {children}
    </ContentFrame>
  );

  return trackIntersection ? (
    <InView
      threshold={1}
      triggerOnce
      onChange={(inView) => {
        if (inView) {
          onCarouselInView({
            supplier,
            supplierTileOrder,
            viewedLocation,
            refSupplier,
            recommendationEngineUsed,
            recommendationEngineVersion,
          });
        }
      }}>
      {({ ref }) => <div ref={ref}>{card}</div>}
    </InView>
  ) : (
    card
  );
};

export default SupplierCard;
